import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'classnames';
import { Text, Title } from '@dropbox/dig-components/typography';
import { CelebrationHighFiveSpot } from '@dropbox/dig-illustrations/dist/cjs/spot/celebration-high-five';
import { Button } from '@dropbox/dig-components/buttons';

import { CountryCodeProvider } from 'signer-app/authentication/use-country-code';
import MinimalLayout from 'signer-app/parts/minimal-layout';
import { getWebflowUrl } from 'signer-app/utils/url-helpers';
import styles from 'signer-app/pages/confirmation-page/confirmation-page.module.css';
import { DropboxSignUpsell } from 'signer-app/pages/confirmation-page/components/dropbox-sign-upsell';
import { CheckmarkCirclePictogram } from '@dropbox/dig-icons/assets';
import { redirectTo } from 'signer-app/utils/redirect';
import {
  createEmbeddedRedirectURL,
  sendEmbeddedFinishMessage,
  sendEmbeddedSignMessage,
} from 'signer-app/pages/confirmation-page/confirmation-page.utils';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { FingerprintJSProvider } from 'signer-app/authentication/fingerprint-js';
import { EmbeddedTerminalPage } from 'signer-app/pages/embedded-terminal-page';

const messages = defineMessages({
  title: {
    id: 'b50e30a30994a6c22e26c8a105262e433c9b8380e457f7f19ab48b2b9a23afde',
    description: 'Confirmation page title',
    defaultMessage: 'Thank you for submitting your signature!',
  },
  subtitle: {
    id: '4c4963aac1f1185b917a3499397230fe4629635325a0dd131bfef2d09191570d',
    description: 'Confirmation page subtitle',
    defaultMessage: "You'll receive a copy in your inbox shortly.",
  },
  close: {
    id: 'c79db4347d9b2755513f4eb1589cc9df91ce989c9210dd76b811f3f906f07b15',
    description: 'Confirmation page, close button for embedded view',
    defaultMessage: 'Close',
  },
  embeddedTitle: {
    id: '723a498f5d5ebe794eea236efab8ded2345b914e22ff845b9e50a1fbe7e9b4d0',
    description: 'generic greeting text for submitting an embedded document',
    defaultMessage: 'Thanks for submitting your document!',
  },
});

function ExternalRedirect({ redirectUrl }: { redirectUrl: string }): null {
  useEffect(() => {
    redirectTo(redirectUrl);
  }, [redirectUrl]);

  return null;
}

// Only Sign can sign, it wouldn't make sense for Fax to be here.
export const siteCode = 'S';

type NonEmbeddedProps = {
  redirectUrl?: string;
};

function NonEmbeddedConfirmationPage({ redirectUrl }: NonEmbeddedProps) {
  const intl = useIntl();
  const isFingerprintEnabled = useFeatureFlag('is_fingerprint_enabled');

  const useFingerprintJS = Boolean(isFingerprintEnabled);

  if (redirectUrl) {
    return <ExternalRedirect redirectUrl={redirectUrl} />;
  }

  return (
    <FingerprintJSProvider enabled={useFingerprintJS}>
      <MinimalLayout
        locale={intl.locale}
        siteCode={siteCode}
        contentCentered
        logoHref={getWebflowUrl('', siteCode)}
        variant="900"
      >
        <div className={styles.center}>
          <Title className={cx(styles.title, styles.mb1)}>
            {intl.formatMessage(messages.title)}
          </Title>
          <Text tagName="div" className={styles.marginBottom} size="large">
            {intl.formatMessage(messages.subtitle)}
          </Text>
          <div className={cx(styles.illustrationContainer, styles.center)}>
            <CelebrationHighFiveSpot />
          </div>
          <CountryCodeProvider>
            <DropboxSignUpsell />
          </CountryCodeProvider>
        </div>
      </MinimalLayout>
    </FingerprintJSProvider>
  );
}

type EmbeddedProps = {
  parentUrl: string;

  /**
   * Redirect URL for either embedded or non-embedded flows
   */
  redirectUrl?: string;

  /**
   * Embedded signature ID
   */
  signatureId: string;
};

function EmbeddedConfirmationPage({
  parentUrl,
  signatureId,
  redirectUrl,
}: EmbeddedProps) {
  const intl = useIntl();
  const closeIFrame = useCallback(() => {
    sendEmbeddedFinishMessage(parentUrl);
  }, [parentUrl]);

  useEffect(() => {
    sendEmbeddedSignMessage({ signatureId, parentUrl });
  }, [signatureId, parentUrl]);

  if (redirectUrl) {
    return (
      <ExternalRedirect
        redirectUrl={createEmbeddedRedirectURL({ redirectUrl, signatureId })}
      />
    );
  }

  return (
    <EmbeddedTerminalPage
      pictogram={CheckmarkCirclePictogram}
      title={intl.formatMessage(messages.embeddedTitle)}
    >
      <Button onClick={closeIFrame} variant="primary">
        {intl.formatMessage(messages.close)}
      </Button>
    </EmbeddedTerminalPage>
  );
}

type Props = {
  isEmbedded?: boolean;
  parentUrl?: string;

  /**
   * Redirect URL for either embedded or non-embedded flows
   */
  redirectUrl?: string;

  /**
   * Embedded signature ID
   */
  signatureId?: string;
};

export default function ConfirmationPage({
  isEmbedded,
  parentUrl,
  redirectUrl,
  signatureId,
}: Props) {
  return isEmbedded && parentUrl && signatureId ? (
    <EmbeddedConfirmationPage
      parentUrl={parentUrl}
      signatureId={signatureId}
      redirectUrl={redirectUrl}
    />
  ) : (
    <NonEmbeddedConfirmationPage redirectUrl={redirectUrl} />
  );
}
